/**
 * TaskSummaryTab - Aba de resumo da tarefa
 * 
 * Layout idêntico ao DealSummaryTab:
 * - Descrição rica editável (TaskDescriptionSection)
 * - Timeline de andamentos (TaskActivityTimeline)
 * 
 * @version 2.0.0
 * RELEASE: 2026-04-06 v1.0.0 - Criação inicial
 * RELEASE: 2026-04-07 v2.0.0 - Redesign: removidos cards, adicionada descrição rica + timeline
 */
import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { sanitizeHtml } from '@/lib/sanitize';
import { TaskDescriptionSection } from '../components/TaskDescriptionSection';
import { TaskActivityTimeline } from '../components/TaskActivityTimeline';
import { TaskChecklistSection } from '../checklist/TaskChecklistSection';
import type { TaskDetail, TaskComment } from '@/hooks/useTaskDetails';

/** Props do componente */
interface TaskSummaryTabProps {
  task: TaskDetail;
  /** Permissão de edição baseada na fase e contexto de responsável */
  canEdit?: boolean;
  comments: TaskComment[];
  isLoadingComments: boolean;
  onAddComment?: (params: { content: string; contentHtml?: string; mentionedUserIds?: string[] }) => Promise<string>;
  isAddingComment: boolean;
  onEditComment?: (params: { commentId: string; content: string; contentHtml?: string }) => Promise<void>;
  isEditingComment?: boolean;
  onDeleteComment?: (commentId: string) => Promise<void>;
  /** Se pode comentar (tarefa não fechada) */
  canComment?: boolean;
  /** Conteúdo da sidebar para exibir inline (usado no mobile) */
  sidebarContent?: React.ReactNode;
}

/**
 * Aba de resumo - padrão visual do DealSummaryTab
 */
export function TaskSummaryTab({
  task,
  canEdit,
  comments,
  isLoadingComments,
  onAddComment,
  isAddingComment,
  onEditComment,
  isEditingComment,
  onDeleteComment,
  canComment,
  sidebarContent,
}: TaskSummaryTabProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  /** Handler para salvar descrição HTML */
  const handleSaveDescription = useCallback(async (html: string) => {
    try {
      const sanitized = sanitizeHtml(html);

      const { error } = await supabase.rpc('update_task', {
        p_id: task.id,
        p_description_html: sanitized || null,
        p_tenant_hint: task.tenant_id,
      });

      if (error) throw error;

      queryClient.invalidateQueries({ queryKey: ['task-details', task.id] });

      toast({
        title: 'Descrição salva',
        description: 'A descrição foi atualizada com sucesso',
      });
    } catch (error) {
      toast({
        title: 'Erro ao salvar',
        description: 'Não foi possível salvar a descrição',
        variant: 'destructive',
      });
      throw error;
    }
  }, [task.id, task.tenant_id, queryClient, toast]);

  return (
    <div className="space-y-4">
      {/* Descrição rica - padrão DealDescriptionAndAttachments */}
      <TaskDescriptionSection
        taskId={task.id}
        content={task.description_html}
        plainContent={task.description}
        readOnly={task.status === 'completed' || task.status === 'cancelled' || !canEdit}
        onSave={handleSaveDescription}
      />

      {/* Sidebar inline — exibida no mobile entre descrição e andamentos */}
      {sidebarContent && (
        <div className="border-t border-b py-2">
          {sidebarContent}
        </div>
      )}

      {/* Checklist (Lista de Verificação) — antes da timeline de andamentos */}
      <TaskChecklistSection
        taskId={task.id}
        tenantId={task.tenant_id}
        readOnly={task.status === 'completed' || task.status === 'cancelled' || !canEdit}
      />

      {/* Timeline de andamentos - padrão DealActivityTimelinePremium */}
      <div>
        <div className="p-3">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              Andamentos
            </h4>
          </div>
          <div className="pb-4">
            <TaskActivityTimeline
              taskId={task.id}
              tenantId={task.tenant_id}
              comments={comments}
              isLoading={isLoadingComments}
              onAddComment={onAddComment}
              isAdding={isAddingComment}
              onEditComment={onEditComment}
              isEditingComment={isEditingComment}
              onDeleteComment={onDeleteComment}
              canComment={canComment}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
